import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API, useAuth } from "../context/AuthContext";
import { ensureAuthToken } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { Loader2, ChevronRight, Check, Sparkles, Search as SearchIcon } from "lucide-react";

const Step = ({ n, title, active, done }) => (
  <div className="flex items-center gap-3">
    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
      ${done ? "bg-[#2C4C3B] text-white" : active ? "bg-[#E6F0E9] text-[#2C4C3B] border border-[#2C4C3B]" : "bg-[#F4F5F0] text-[#52606D]"}`}>
      {done ? <Check className="w-4 h-4" /> : n}
    </div>
    <span className={`text-sm hidden sm:inline ${active || done ? "text-[#2C4C3B] font-medium" : "text-[#52606D]"}`}>{title}</span>
  </div>
);

const isArticulationDifference = (d) => d?.slug === "articulation-disorder";
const isGlpDifference = (d) => d?.slug === "gestalt-language-processing";

export default function Search() {
  const { authHeaders, user, openAuth, refreshMe } = useAuth();
  const nav = useNavigate();
  const [activities, setActivities] = useState([]);
  const [differences, setDifferences] = useState([]);
  const [communicators, setCommunicators] = useState([]);
  const [severities, setSeverities] = useState([]);

  const [selectedActivityIds, setSelectedActivityIds] = useState([]);
  const [customActivity, setCustomActivity] = useState("");
  const [useCustom, setUseCustom] = useState(false);
  const [activityFilter, setActivityFilter] = useState("");
  const [ageBand, setAgeBand] = useState(""); // "child" | "adult"
  const [difference, setDifference] = useState(null);
  const [communicator, setCommunicator] = useState(null);
  const [severity, setSeverity] = useState(null);
  const [device, setDevice] = useState("generic");
  const [country, setCountry] = useState("");
  const [region, setRegion] = useState("");
  // Conditional clinical inputs
  const [artSound, setArtSound] = useState("");
  const [artPosition, setArtPosition] = useState("");
  const [glpScripts, setGlpScripts] = useState("");
  const [generating, setGenerating] = useState(false);

  const isSubscribed = !!user?.is_subscribed;
  const PLAY_CAP = isSubscribed ? 3 : 2;
  const ROUTINE_CAP = 1;

  const selectedActivities = useMemo(
    () => activities.filter((a) => selectedActivityIds.includes(a.id)),
    [activities, selectedActivityIds]
  );
  const selectedPlayCount = selectedActivities.filter((a) => a.category === "play").length;
  const selectedRoutineCount = selectedActivities.filter((a) => a.category === "routine").length;

  const isSelected = (a) => selectedActivityIds.includes(a.id);
  const isCapped = (a) => {
    if (isSelected(a)) return false;
    if (a.category === "play") return selectedPlayCount >= PLAY_CAP;
    if (a.category === "routine") return selectedRoutineCount >= ROUTINE_CAP;
    return false;
  };
  const toggleActivity = (a) => {
    if (isSelected(a)) {
      setSelectedActivityIds((prev) => prev.filter((id) => id !== a.id));
      return;
    }
    if (a.category === "play" && selectedPlayCount >= PLAY_CAP) {
      toast(isSubscribed
        ? `Up to ${PLAY_CAP} play activities allowed.`
        : `Free users can select up to ${PLAY_CAP} play activities. Subscribe to unlock 3.`);
      return;
    }
    if (a.category === "routine" && selectedRoutineCount >= ROUTINE_CAP) {
      toast(`Only 1 routine can be combined per generation.`);
      return;
    }
    setSelectedActivityIds((prev) => [...prev, a.id]);
  };

  useEffect(() => {
    (async () => {
      const [a, d, c] = await Promise.all([
        axios.get(`${API}/activities`),
        axios.get(`${API}/disorders`),
        axios.get(`${API}/communicator-stages`),
      ]);
      setActivities(a.data);
      setDifferences(d.data);
      setCommunicators(c.data);
    })();
  }, []);

  // load severities when difference changes
  useEffect(() => {
    if (!difference) { setSeverities([]); setSeverity(null); return; }
    (async () => {
      const r = await axios.get(`${API}/severities`, { params: { disorder_id: difference.id } });
      setSeverities(r.data);
      setSeverity(null);
    })();
  }, [difference]);

  const playActs = useMemo(() => {
    const q = activityFilter.trim().toLowerCase();
    return activities.filter((a) => a.category === "play" && (!q || a.name.toLowerCase().includes(q) || a.description.toLowerCase().includes(q)));
  }, [activities, activityFilter]);
  const routineActs = useMemo(() => {
    const q = activityFilter.trim().toLowerCase();
    return activities.filter((a) => a.category === "routine" && (!q || a.name.toLowerCase().includes(q) || a.description.toLowerCase().includes(q)));
  }, [activities, activityFilter]);

  const activityChosen = useCustom ? customActivity.trim().length > 1 : selectedActivityIds.length > 0;

  // Wizard step calculator with new Age Band step inserted after Activity
  const step = !activityChosen ? 1
    : !ageBand ? 2
    : !difference ? 3
    : !communicator ? 4
    : !severity ? 5
    : 6;

  const generate = async () => {
    if (!activityChosen || !ageBand || !difference || !communicator || !severity) return;
    // Trial gate: any non-subscribed user gets ONE trial generation.
    if (!user?.is_subscribed && (user?.generations_used || 0) >= 1) {
      toast("You've used your free trial — subscribe to keep generating.", { description: "Redirecting to subscription plans…" });
      setTimeout(() => nav("/plans"), 900);
      return;
    }
    setGenerating(true);
    try {
      const token = await ensureAuthToken();
      const headers = { Authorization: `Bearer ${token}` };
      const body = {
        disorder_id: difference.id,
        communicator_stage_id: communicator.id,
        severity_id: severity.id,
        device,
        user_age_band: ageBand,
      };
      if (useCustom) body.custom_activity_name = customActivity.trim();
      else body.activity_ids = selectedActivityIds;
      if (country.trim()) body.location_country = country.trim();
      if (region.trim()) body.location_region = region.trim();
      if (isArticulationDifference(difference) && artSound.trim() && artPosition) {
        body.articulation_target_sound = artSound.trim();
        body.articulation_position = artPosition;
      }
      if (isGlpDifference(difference) && glpScripts.trim()) {
        body.glp_existing_scripts = glpScripts.trim();
      }
      const res = await axios.post(`${API}/generate`, body, { headers });
      toast.success(res.data.is_trial ? "Free trial list ready — scroll down to unlock the rest" : "Word list ready");
      await refreshMe(); // refresh generations_used
      nav(`/results/${res.data.id}`);
    } catch (err) {
      if (err?.response?.status === 402) {
        toast(err.response.data?.detail || "Subscribe to continue using CommPass.");
        nav("/plans");
      } else {
        toast.error(err?.response?.data?.detail || "Generation failed");
      }
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-14" data-testid="search-page">
      <span className="text-xs tracking-[0.2em] uppercase text-[#C66A51]">Programming · Activity Search</span>
      <h1 className="font-display text-4xl font-semibold text-[#2C4C3B] mt-1">Build your word list</h1>
      <p className="text-[#52606D] mt-2 max-w-2xl">Seven steps: pick an activity, set the age band, choose the differences profile, AAC communicator stage, severity, location and device. We&rsquo;ll generate a clinical programming table grouped by communicative function.</p>

      <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 p-4 rounded-2xl bg-[#F4F5F0] border border-[#E2E8F0]" data-testid="search-stepper">
        <Step n={1} title="Activity" active={step === 1} done={activityChosen} />
        <ChevronRight className="w-4 h-4 text-[#A8BAA3]" />
        <Step n={2} title="Age band" active={step === 2} done={!!ageBand} />
        <ChevronRight className="w-4 h-4 text-[#A8BAA3]" />
        <Step n={3} title="Differences" active={step === 3} done={!!difference} />
        <ChevronRight className="w-4 h-4 text-[#A8BAA3]" />
        <Step n={4} title="Communicator" active={step === 4} done={!!communicator} />
        <ChevronRight className="w-4 h-4 text-[#A8BAA3]" />
        <Step n={5} title="Severity" active={step === 5} done={!!severity} />
        <ChevronRight className="w-4 h-4 text-[#A8BAA3]" />
        <Step n={6} title="Location & device" active={step === 6} done={false} />
      </div>

      {/* Step 1: Activity */}
      <section className="mt-12" data-testid="step-activity">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-2xl text-[#2C4C3B]">1. Select or type an activity</h2>
          <button
            onClick={() => { setUseCustom(!useCustom); setSelectedActivityIds([]); }}
            className="text-sm text-[#2C4C3B] hover:underline"
            data-testid="toggle-custom-activity">
            {useCustom ? "← Browse catalog" : "Type your own →"}
          </button>
        </div>

        {useCustom ? (
          <div className="p-6 rounded-2xl bg-white border border-[#E2E8F0]">
            <label className="text-xs uppercase tracking-[0.15em] text-[#52606D]">Custom activity</label>
            <Input
              value={customActivity}
              onChange={(e) => setCustomActivity(e.target.value)}
              placeholder="e.g., Trampoline jumping, Birthday party, Park visit…"
              className="mt-2 rounded-xl"
              data-testid="custom-activity-input"
            />
            <p className="text-xs text-[#52606D] mt-2">The AI will generate AAC vocabulary tailored to whatever activity you type.</p>
          </div>
        ) : (
          <>
            <div className="relative mb-6 max-w-md" data-testid="activity-filter-wrap">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52606D]" />
              <Input
                value={activityFilter}
                onChange={(e) => setActivityFilter(e.target.value)}
                placeholder="Search activities — e.g., bubbles, mealtime, puzzles…"
                className="pl-9 rounded-full bg-white border-[#E2E8F0]"
                data-testid="activity-filter-input"
              />
            </div>
            {playActs.length === 0 && routineActs.length === 0 && (
              <div className="p-6 rounded-2xl border border-dashed border-[#E2E8F0] text-center text-[#52606D]" data-testid="activity-empty">
                No matches. Try a different search or <button onClick={() => { setUseCustom(true); setCustomActivity(activityFilter); }} className="text-[#2C4C3B] font-medium hover:underline">type it as a custom activity →</button>
              </div>
            )}
            {playActs.length > 0 && (
              <>
                <h3 className="text-xs uppercase tracking-[0.2em] text-[#C66A51] mb-3">Play activities <span className="text-[#52606D] normal-case tracking-normal">· tap to select multiple (max {PLAY_CAP}{!isSubscribed && " — subscribe to unlock 3"}) · add up to 1 routine below</span></h3>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {playActs.map((a) => {
                    const capped = isCapped(a);
                    return (
                      <button key={a.id} onClick={() => toggleActivity(a)}
                        className={`text-left p-5 rounded-2xl border card-lift ${isSelected(a) ? "border-[#2C4C3B] bg-[#E6F0E9]" : capped ? "border-[#E2E8F0] bg-[#F4F5F0] opacity-50 cursor-not-allowed" : "border-[#E2E8F0] bg-white"}`}
                        data-testid={`activity-option-${a.slug}`}>
                        <div className="flex items-center justify-between">
                          <div className="font-display text-lg text-[#2C4C3B]">{a.name}</div>
                          {isSelected(a) && <Check className="w-4 h-4 text-[#2C4C3B]" />}
                        </div>
                        <div className="text-xs text-[#52606D] mt-1 line-clamp-2">{a.description}</div>
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            {routineActs.length > 0 && (
              <>
                <h3 className="text-xs uppercase tracking-[0.2em] text-[#C66A51] mt-10 mb-3">Routines <span className="text-[#52606D] normal-case tracking-normal">· pick up to {ROUTINE_CAP} to combine with play</span></h3>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {routineActs.map((a) => {
                    const capped = isCapped(a);
                    return (
                      <button key={a.id} onClick={() => toggleActivity(a)}
                        className={`text-left p-5 rounded-2xl border card-lift ${isSelected(a) ? "border-[#2C4C3B] bg-[#E6F0E9]" : capped ? "border-[#E2E8F0] bg-[#F4F5F0] opacity-50 cursor-not-allowed" : "border-[#E2E8F0] bg-white"}`}
                        data-testid={`activity-option-${a.slug}`}>
                        <div className="flex items-center justify-between">
                          <div className="font-display text-lg text-[#2C4C3B]">{a.name}</div>
                          {isSelected(a) && <Check className="w-4 h-4 text-[#2C4C3B]" />}
                        </div>
                        <div className="text-xs text-[#52606D] mt-1 line-clamp-2">{a.description}</div>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </>
        )}
      </section>

      {/* Step 2: Age Band */}
      {activityChosen && (
        <section className="mt-12" data-testid="step-age-band">
          <h2 className="font-display text-2xl text-[#2C4C3B] mb-2">2. Age band</h2>
          <p className="text-sm text-[#52606D] mb-4">Vocabulary and modelling register adjust to age. Adult outputs avoid infantilising language; child outputs stay developmentally appropriate.</p>
          <div className="grid sm:grid-cols-2 gap-3 max-w-3xl">
            {[
              { code: "child", title: "Child", desc: "Concrete, sensory, play- and routine-based vocabulary." },
              { code: "adult", title: "Adult", desc: "Vocation, autonomy, community, identity. Adult register throughout." },
            ].map((opt) => (
              <button key={opt.code} onClick={() => setAgeBand(opt.code)}
                className={`text-left p-5 rounded-2xl border card-lift ${ageBand === opt.code ? "border-[#2C4C3B] bg-[#E6F0E9]" : "border-[#E2E8F0] bg-white"}`}
                data-testid={`age-band-option-${opt.code}`}>
                <div className="font-display text-lg text-[#2C4C3B]">{opt.title}</div>
                <div className="text-xs text-[#52606D] mt-1">{opt.desc}</div>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Step 3: Differences */}
      {activityChosen && ageBand && (
        <section className="mt-12" data-testid="step-differences">
          <h2 className="font-display text-2xl text-[#2C4C3B] mb-4">3. Choose differences profile</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {differences.filter((d) => !d.coming_soon).map((d) => (
              <button key={d.id} onClick={() => { setDifference(d); setCommunicator(null); }}
                className={`text-left p-5 rounded-2xl border card-lift ${difference?.id === d.id ? "border-[#2C4C3B] bg-[#E6F0E9]" : "border-[#E2E8F0] bg-white"}`}
                data-testid={`difference-option-${d.slug}`}>
                <div className="font-display text-lg text-[#2C4C3B]">{d.name}</div>
                <div className="text-xs text-[#52606D] mt-1 line-clamp-2">{d.description}</div>
              </button>
            ))}
          </div>

          {/* Adding soon — disabled cards */}
          {differences.some((d) => d.coming_soon) && (
            <div className="mt-10" data-testid="coming-soon-section">
              <h3 className="text-xs uppercase tracking-[0.2em] text-[#C66A51] mb-3">
                Adding soon <span className="text-[#52606D] normal-case tracking-normal">· these profiles are coming in an upcoming release</span>
              </h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {differences.filter((d) => d.coming_soon).map((d) => (
                  <div key={d.id}
                    className="relative text-left p-5 rounded-2xl border border-dashed border-[#E2E8F0] bg-[#F4F5F0] opacity-70 cursor-not-allowed select-none"
                    data-testid={`difference-coming-soon-${d.slug}`}
                    aria-disabled="true">
                    <span className="absolute top-3 right-3 px-2 py-0.5 rounded-full text-[10px] tracking-[0.15em] uppercase bg-[#C66A51]/10 text-[#C66A51] font-semibold">Adding soon</span>
                    <div className="font-display text-lg text-[#52606D] pr-20">{d.name}</div>
                    <div className="text-xs text-[#52606D] mt-1 line-clamp-2">{d.description}</div>
                  </div>
                ))}
              </div>
              <p className="mt-5 text-sm text-[#52606D] italic" data-testid="more-differences-line">
                More differences profiles are being added regularly. Have a profile you&rsquo;d like prioritised? Tell us on the <a href="/about" className="text-[#2C4C3B] font-medium hover:underline">About page</a>.
              </p>
            </div>
          )}

          {/* Conditional clinical fields */}
          {isArticulationDifference(difference) && (
            <div className="mt-6 p-6 rounded-2xl bg-[#FDF6F0] border border-[#C66A51]/30" data-testid="articulation-fields">
              <h3 className="font-display text-lg text-[#2C4C3B]">Articulation — target sound (optional)</h3>
              <p className="text-xs text-[#52606D] mt-1">Enter a target sound and position to activate the phoneme filter on the generated list.</p>
              <div className="grid sm:grid-cols-2 gap-3 mt-4">
                <div>
                  <label className="text-xs uppercase tracking-[0.15em] text-[#52606D]">Target sound</label>
                  <Input
                    value={artSound}
                    onChange={(e) => setArtSound(e.target.value)}
                    placeholder="e.g., /s/, /k/, /r/, /sh/"
                    className="mt-2 rounded-xl"
                    data-testid="articulation-sound-input"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.15em] text-[#52606D]">Position</label>
                  <select
                    value={artPosition}
                    onChange={(e) => setArtPosition(e.target.value)}
                    className="mt-2 w-full rounded-xl border border-[#E2E8F0] bg-white px-4 py-2.5 text-sm text-[#1A202C] focus:outline-none focus:ring-2 focus:ring-[#2C4C3B]/30"
                    data-testid="articulation-position-select">
                    <option value="">— Select position —</option>
                    <option value="initial">Initial</option>
                    <option value="medial">Medial</option>
                    <option value="final">Final</option>
                  </select>
                </div>
              </div>
            </div>
          )}
          {isGlpDifference(difference) && (
            <div className="mt-6 p-6 rounded-2xl bg-[#FDF6F0] border border-[#C66A51]/30" data-testid="glp-fields">
              <h3 className="font-display text-lg text-[#2C4C3B]">GLP — existing scripts (optional)</h3>
              <p className="text-xs text-[#52606D] mt-1">Enter the child&rsquo;s current gestalts/scripts (one per line or comma-separated). The AI will map functions and supplement with activity-relevant gestalts.</p>
              <textarea
                value={glpScripts}
                onChange={(e) => setGlpScripts(e.target.value)}
                rows={4}
                placeholder={"e.g.,\nLet's do it again\nAll done\nReady, set, go"}
                className="mt-3 w-full rounded-xl border border-[#E2E8F0] bg-white p-3 text-sm text-[#1A202C] focus:outline-none focus:ring-2 focus:ring-[#2C4C3B]/30"
                data-testid="glp-scripts-input"
              />
            </div>
          )}
        </section>
      )}

      {/* Step 4: Communicator Stage */}
      {activityChosen && ageBand && difference && (
        <section className="mt-12" data-testid="step-communicator">
          <h2 className="font-display text-2xl text-[#2C4C3B] mb-4">4. AAC communicator stage</h2>
          <p className="text-sm text-[#52606D] mb-4">Based on Trina Becker&rsquo;s framework — determines core/fringe ratio.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {communicators.map((c) => (
              <button key={c.id} onClick={() => setCommunicator(c)}
                className={`text-left p-5 rounded-2xl border card-lift ${communicator?.id === c.id ? "border-[#2C4C3B] bg-[#E6F0E9]" : "border-[#E2E8F0] bg-white"}`}
                data-testid={`communicator-option-${c.code}`}>
                <span className="text-xs tracking-[0.15em] uppercase text-[#C66A51]">Stage {c.order}</span>
                <div className="font-display text-lg text-[#2C4C3B] mt-1">{c.title}</div>
                <div className="text-xs text-[#52606D] mt-1 line-clamp-2">{c.description}</div>
                <div className="text-xs text-[#2C4C3B] mt-2">Ratio: {c.ratio_core}% core / {c.ratio_fringe}% fringe</div>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Step 5: Severity */}
      {activityChosen && ageBand && difference && communicator && (
        <section className="mt-12" data-testid="step-severity">
          <h2 className="font-display text-2xl text-[#2C4C3B] mb-4">5. {difference.severity_kind === "glp_stages" ? "GLP stage" : "Severity / stage"}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {severities.map((s) => (
              <button key={s.id} onClick={() => setSeverity(s)}
                className={`text-left p-5 rounded-2xl border card-lift ${severity?.id === s.id ? "border-[#2C4C3B] bg-[#E6F0E9]" : "border-[#E2E8F0] bg-white"}`}
                data-testid={`severity-option-${s.code}`}>
                <div className="font-display text-lg text-[#2C4C3B]">{s.label}</div>
                <div className="text-xs text-[#52606D] mt-1">{s.description}</div>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Step 6: Location & Device */}
      {activityChosen && ageBand && difference && communicator && severity && (
        <section className="mt-12" data-testid="step-location-device">
          <h2 className="font-display text-2xl text-[#2C4C3B] mb-1">6. Location <span className="text-sm text-[#52606D] font-normal">(optional — adds cultural context)</span></h2>
          <p className="text-sm text-[#52606D] mb-4">Tailors fringe vocabulary — e.g., dosa &amp; idli for India / Kerala mealtimes; biscuit &amp; porridge for UK.</p>
          <div className="grid sm:grid-cols-2 gap-3 max-w-3xl">
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-[#52606D]">Country</label>
              <select
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="mt-2 w-full rounded-xl border border-[#E2E8F0] bg-white px-4 py-2.5 text-sm text-[#1A202C] focus:outline-none focus:ring-2 focus:ring-[#2C4C3B]/30"
                data-testid="location-country-select">
                <option value="">— Select country —</option>
                {["India","Singapore","Malaysia","United States","United Kingdom","Australia","Canada","UAE","South Africa","New Zealand","Other"].map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-[#52606D]">State / region (optional)</label>
              <Input
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                placeholder="e.g., Kerala, Tamil Nadu, London, Texas…"
                className="mt-2 rounded-xl"
                data-testid="location-region-input"
              />
            </div>
          </div>

          <h2 className="font-display text-2xl text-[#2C4C3B] mt-10 mb-4">Device</h2>
          <div className="grid sm:grid-cols-2 gap-3 max-w-3xl">
            {[
              { code: "generic", title: "Generic AAC", desc: "Device-agnostic vocabulary suggestions." },
              { code: "avaz", title: "Avaz AAC", desc: "Adds Avaz-specific cues: grammar/morphology, Quick Folder, Alert button." },
            ].map((opt) => (
              <button key={opt.code} onClick={() => setDevice(opt.code)}
                className={`text-left p-5 rounded-2xl border card-lift ${device === opt.code ? "border-[#2C4C3B] bg-[#E6F0E9]" : "border-[#E2E8F0] bg-white"}`}
                data-testid={`device-option-${opt.code}`}>
                <div className="font-display text-lg text-[#2C4C3B]">{opt.title}</div>
                <div className="text-xs text-[#52606D] mt-1">{opt.desc}</div>
              </button>
            ))}
          </div>

          <div className="mt-10 p-8 rounded-2xl bg-[#2C4C3B] text-white flex flex-wrap items-center justify-between gap-4" data-testid="generate-panel">
            <div>
              <div className="text-xs tracking-[0.2em] uppercase opacity-70">Ready</div>
              <div className="font-display text-xl mt-1">
                {useCustom ? customActivity : selectedActivities.map((a) => a.name).join(" + ")} · {difference.name}
              </div>
              <div className="text-sm opacity-80 mt-1">
                {ageBand === "adult" ? "Adult" : "Child"} · {communicator.title} · {severity.label} · {device === "avaz" ? "Avaz" : "Generic AAC"}
                {(country || region) && <> · {[region, country].filter(Boolean).join(", ")}</>}
              </div>
            </div>
            <Button onClick={generate} disabled={generating}
              className="rounded-full bg-[#C66A51] hover:bg-[#A85639] text-white px-7 py-6" data-testid="generate-btn">
              {generating ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Generating…</> : <><Sparkles className="w-4 h-4 mr-2" /> Generate Word List</>}
            </Button>
          </div>
        </section>
      )}
    </div>
  );
}
